import React, { useEffect, useRef, useState } from 'react';

import { useDebounce } from 'src/hooks/useDebounce';

import { Editor, useMonaco } from '@monaco-editor/react';
import { Modal, Spin } from 'antd';
import type { editor } from 'monaco-editor';
import { FaEye, FaPlay } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import { usePostTestSqlObserverMutation } from 'src/redux/services/centralApi';

import {
	editorOptions,
	sensitiveSQL
} from 'src/pages/observers/components/CodeEditor/configs';
import {
	CodeContainer,
	ExecuteButton,
	InnerContainer,
	PreviewButton,
	SQLPreviewModal
} from 'src/pages/observers/components/CodeEditor/styles';
import type {
	IQueryResult,
	ISqlCodeEditorComponent
} from 'src/pages/observers/components/CodeEditor/types';
import { parseSQL } from 'src/pages/observers/components/CodeEditor/utils';
import VariablesTutorial from 'src/pages/observers/components/CodeEditor/VariablesTutorial';

export default function SqlCodeEditor(
	{
		growWithCode,
		readOnly,
		executable,
		autoExecuteMs,
		onCodeChange,
		onQueryFieldsChange,
		onQueryResultChange,
		hasReturn,
		editor_props,
		blur,
		height = '190px',
		conditions,
	}: ISqlCodeEditorComponent
) {
	const [ postTestQuery, ] = usePostTestSqlObserverMutation();
	const [ isTestingQuery, setIsTestingQuery, ] = useState<boolean>(false);
	const [ modal, contextoHolder, ] = Modal.useModal();

	const monaco = useMonaco();
	const [ editor, setEditor, ] = useState<editor.IStandaloneCodeEditor | null>(null);

	const [ editorLoading, setEditorLoading, ] = useState<boolean>(false);
	const [ code, setCode, ] = useState<string>('');
	const [ queryResult, setQueryResult, ] = useState<IQueryResult>();

	const debouncedCode = useDebounce(code, autoExecuteMs ?? 0);
	const isFirstAutoRun = useRef(true);
	const handlePlayRef = useRef<() => Promise<void>>(() => Promise.resolve());
	// id do teste em andamento: um teste novo substitui o anterior,
	// que aborta o polling silenciosamente
	const runIdRef = useRef<string | null>(null);

	useEffect(() => {
		onCodeChange?.(code);
	}, [ code, ]);

	useEffect(() => {
		onQueryResultChange?.(queryResult);
	}, [ queryResult, ]);

	useEffect(() => {
		if (growWithCode) {
			if (!editor || !monaco) return;

			const el = editor.getDomNode();
			if (!el) { return; }

			const lineHeight = editor.getOption(monaco.editor.EditorOption.lineHeight);
			editor.onDidChangeModelDecorations(() => {
				setTimeout(() => {
					const model = editor?.getModel();
					if (!model) { return; }

					const maxHeight = lineHeight * 10;
					const nextHeight = model.getLineCount() * lineHeight;
					el.style.height = nextHeight >= maxHeight
						? maxHeight + 'px' : nextHeight + 'px';

					editor.layout();
				}, 0);
			});
		}
	}, [ editor, monaco, code, ]);

	useEffect(() => {
		if (editor_props?.value) handleValueChange(editor_props.value.toString());
	}, [ editor_props, ]);

	useEffect(() => {
		if (!autoExecuteMs || !debouncedCode) return;
		if (isFirstAutoRun.current) {
			isFirstAutoRun.current = false;
			return;
		}
		handlePlayRef.current();
	}, [ debouncedCode, ]);

	const handleValueChange = (value: string | undefined) => {
		if (value === undefined) {
			setCode('');
			return;
		}

		let _code = value;
		sensitiveSQL.forEach((sensitive) => {
			const lowerSensitive = sensitive.toLowerCase();
			_code = _code.replaceAll(lowerSensitive, '');
			_code = _code.replaceAll(sensitive, '');
		});

		if (_code !== value) {
			toast.warn('Apenas consultas podem ser feitas no SQL!');
		}

		if (_code !== code) {
			setCode(_code);
		}
	};

	// o teste roda de forma assíncrona: o POST só registra a consulta e o
	// resultado é buscado por polling, para a query poder demorar mais que
	// o limite de ~100s que o Cloudflare impõe em requisições abertas.
	// o polling usa fetch direto (sem RTK Query) porque cache de resultado
	// aqui é indesejado e o ciclo de vida do hook interferia nas respostas
	const SQL_TEST_POLL_INTERVAL_MS = 3000;
	const SQL_TEST_MAX_WAIT_MS = 10 * 60 * 1000;

	type ITestSqlPollResponse = {
		payload?:
			| { status: 'pending' }
			| {
				status: 'done';
				result: {
					result: string | null;
					query: { type: string; field: string }[];
					executionMS: number;
				};
			};
	};

	const pollTestSqlResult = async (id: string): Promise<ITestSqlPollResponse> => {
		const response = await fetch(
			`${process.env.REACT_APP_API_URL}/observers/test-sql/${id}`,
			{
				headers: {
					Authorization: `Bearer ${Cookies.get('intraguard.token')}`,
				},
			}
		);
		const body = await response.json().catch(() => undefined);
		// mesmo formato de erro do RTK Query, para o catch tratar igual
		if (!response.ok) throw { status: response.status, data: body, };
		return body;
	};

	const handlePlay = async () => {
		if (!code) {
			toast.info(`É necessário uma consulta SQL!`);
			return;
		}

		const sql = parseSQL(code, conditions);
		if(!sql) return;

		setQueryResult(undefined);
		setIsTestingQuery(true);

		const sleep = (ms: number) =>
			new Promise((resolve) => setTimeout(resolve, ms));

		const id = window.crypto.randomUUID();
		runIdRef.current = id;

		try {
			await postTestQuery({
				query: sql.trim(),
				id,
			}).unwrap();

			const startedAt = Date.now();
			let consecutiveFailures = 0;
			while (Date.now() - startedAt < SQL_TEST_MAX_WAIT_MS) {
				await sleep(SQL_TEST_POLL_INTERVAL_MS);
				if (runIdRef.current !== id) return;

				let payload: ITestSqlPollResponse['payload'];
				try {
					const body = await pollTestSqlResult(id);
					payload = body?.payload;
					if (!payload) {
						console.error('Resposta sem payload no teste SQL:', body);
					}
					consecutiveFailures = payload ? 0 : consecutiveFailures + 1;
				} catch (err: any) {
					if (err?.data?.frontend_message === 'syntax') {
						onQueryFieldsChange?.([]);
						// detail: motivo da recusa pelo agente (ex.: comando de escrita)
						toast.error(err?.data?.payload?.detail ?? 'Existe um erro de sintaxe no código!');
						return;
					}

					console.error(err);
					consecutiveFailures++;
					// 404 = API sem a rota de resultado (versão antiga)
					if (err?.status === 404) consecutiveFailures = 5;
				}

				// falhas seguidas = problema persistente, não transitório
				if (consecutiveFailures >= 5) {
					onQueryFieldsChange?.([]);
					toast.error('Não foi possível consultar o resultado do teste!');
					return;
				}
				if (!payload) continue;

				if (payload.status === 'pending') continue;

				// processamento fora do try do polling: se algo falhar aqui,
				// cai no catch externo (toast + spinner liberado) em vez de
				// voltar para o loop
				const queryFields = payload.result.query;
				const executionInterval = payload.result.executionMS;
				const data = payload.result.result
					? JSON.parse(payload.result.result) : null;
				setQueryResult({ data, executionInterval, });

				if (executionInterval > 10000) {
					toast.error(
						`A consulta está muito longa, é necessário otimizá-la!`
					);
					return;
				}

				onQueryFieldsChange?.(queryFields);
				return;
			}

			onQueryFieldsChange?.([]);
			toast.error('Tempo limite de requisição excedido!');
		} catch (err) {
			console.error(err);
			onQueryFieldsChange?.([]);
			toast.error('Ocorreu um erro ao testar sua consulta!');
		} finally {
			if (runIdRef.current === id) setIsTestingQuery(false);
		}
	};
	// Sempre mantém a ref apontando para o último closure de handlePlay
	handlePlayRef.current = handlePlay;

	const onEditorMount = (editor: editor.IStandaloneCodeEditor): void => {
		setEditorLoading(false);
		setEditor(editor);
	};

	return (
		<CodeContainer
			style={{
				borderRadius: hasReturn ? '6px 6px 0 0' : '6px',
				cursor: 'not-allowed !important',
				userSelect: 'none',
				WebkitUserSelect: 'none',
				msUserSelect: 'none',
				MozUserSelect: 'none',
				position: 'relative',
			}}
		>
			{ blur && <InnerContainer /> }
			{ editorLoading && <Spin /> }
			{
				executable &&
				<>
					<PreviewButton
						onClick={() => {
							modal.confirm({
								width: 600,
								cancelButtonProps: {
									style: { display: 'none', },
								},
								title: 'SQL Final',
								content: <SQLPreviewModal>
									{
										parseSQL(code, conditions)
											.split('\n')
											.map((line, i) => (
												<p key={i}>{line}</p>
											))
									}
								</SQLPreviewModal>,
							});
						}}
						type='text'
						icon={<FaEye />}
					/>
					<VariablesTutorial />
					<ExecuteButton
						type='text'
						icon={<FaPlay />}
						onClick={handlePlay}
						loading={isTestingQuery}
					/>
				</>
			}
			<Editor
				value={code}
				language={'sql'}
				onMount={onEditorMount}
				options={{
					...editorOptions,
					readOnly: readOnly || blur,
				}}
				onChange={handleValueChange}
				height={growWithCode ? undefined : height}
				{...editor_props}
			/>
			{contextoHolder}
		</CodeContainer>
	);
}
